// SPV Pool v1.0 PR 5 — TanStack Query hooks for the four service-provider
// counterparty entities. A single set of generic hooks is parameterised by
// ServiceProviderType so the shared list / detail components stay DRY.
//
// The four typed transport objects return distinct DTO shapes; the hooks
// normalise to the AnyServiceProviderDto union so callers get one stable
// type regardless of provider type.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import type { PagedResult } from "@/types/api";
import { providerApiFor } from "@/api/serviceProviders";
import type {
  AnyServiceProviderDto,
  ServiceProviderListQuery,
  ServiceProviderType,
  ServiceProviderUpdate,
} from "@/types/serviceProviders";

type ProviderCreateBody = Record<string, unknown>;

export const providerKeys = {
  all: (type: ServiceProviderType) => ["service-providers", type] as const,
  list: (type: ServiceProviderType, query: ServiceProviderListQuery | undefined) =>
    [...providerKeys.all(type), "list", query ?? {}] as QueryKey,
  detail: (type: ServiceProviderType, id: string) =>
    [...providerKeys.all(type), "detail", id] as const,
  poolsServed: (type: ServiceProviderType, id: string) =>
    [...providerKeys.all(type), "pools-served", id] as const,
};

export function useProviderList(
  type: ServiceProviderType,
  query?: ServiceProviderListQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: providerKeys.list(type, query),
    queryFn: () =>
      providerApiFor(type).list(query) as Promise<
        PagedResult<AnyServiceProviderDto>
      >,
    enabled,
  });
}

export function useProvider(
  type: ServiceProviderType,
  id: string | undefined,
) {
  return useQuery({
    queryKey: providerKeys.detail(type, id ?? ""),
    queryFn: () =>
      providerApiFor(type).get(id!) as Promise<AnyServiceProviderDto>,
    enabled: !!id,
  });
}

export function usePoolsServedByProvider(
  type: ServiceProviderType,
  id: string | undefined,
) {
  return useQuery({
    queryKey: providerKeys.poolsServed(type, id ?? ""),
    queryFn: () => providerApiFor(type).poolsServed(id!),
    enabled: !!id,
  });
}

export function useCreateProvider(type: ServiceProviderType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProviderCreateBody) =>
      // The four create-DTO shapes differ; callers pass the matching body.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      providerApiFor(type).create(body as any) as Promise<AnyServiceProviderDto>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: providerKeys.all(type) });
    },
  });
}

export function useUpdateProvider(type: ServiceProviderType, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ServiceProviderUpdate) =>
      providerApiFor(type).update(id, body) as Promise<AnyServiceProviderDto>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: providerKeys.detail(type, id) });
      qc.invalidateQueries({ queryKey: providerKeys.all(type) });
    },
  });
}
